import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { diffAuditFields, parseAuditValue, summarizeAuditFields } from './auditDetailFields';

const cellSx = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  maxWidth: '300px',
  whiteSpace: 'normal',
};

const FieldDiff = ({ changes }) => (
  <Box sx={cellSx}>
    {changes.map((change) => (
      <Typography key={change.key} variant="caption" display="block">
        <Box component="span" sx={{ fontWeight: 600 }}>{`${change.label}: `}</Box>
        <Box component="span" sx={{ color: 'error.main', textDecoration: 'line-through' }}>
          {change.from}
        </Box>
        {' → '}
        <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>{change.to}</Box>
      </Typography>
    ))}
  </Box>
);

FieldDiff.propTypes = {
  changes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      from: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const FieldSummary = ({ payload, removed }) => {
  const { headline, facts } = summarizeAuditFields(payload);
  const headlineSx = removed
    ? { color: 'error.main', fontWeight: 600, textDecoration: 'line-through' }
    : { color: 'success.main', fontWeight: 600 };
  return (
    <Box sx={cellSx}>
      {headline && (
        <Typography variant="caption" display="block" sx={headlineSx}>
          {headline}
        </Typography>
      )}
      {facts.map((fact) => (
        <Typography key={fact.key} variant="caption" display="block" color="text.secondary">
          {`${fact.label}: ${fact.value}`}
        </Typography>
      ))}
    </Box>
  );
};

FieldSummary.propTypes = {
  payload: PropTypes.object.isRequired,
  removed: PropTypes.bool,
};

FieldSummary.defaultProps = {
  removed: false,
};

// Renders one audit entry's Details cell. JSON payloads become a field-level
// before → after diff (or a labelled summary when only one side exists);
// non-JSON values render as plain text.
const AuditTrailDetails = ({ oldValue, newValue }) => {
  const oldObj = parseAuditValue(oldValue);
  const newObj = parseAuditValue(newValue);

  if (oldObj && newObj) {
    const changes = diffAuditFields(oldObj, newObj);
    if (changes.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary" sx={cellSx}>
          Saved with no changes
        </Typography>
      );
    }
    return <FieldDiff changes={changes} />;
  }

  if (newObj) return <FieldSummary payload={newObj} />;
  if (oldObj) return <FieldSummary payload={oldObj} removed />;

  if (oldValue || newValue) {
    return (
      <Box sx={cellSx}>
        {oldValue && (
          <Typography variant="caption" display="block" sx={{ color: 'error.main' }}>
            {oldValue}
          </Typography>
        )}
        {newValue && (
          <Typography variant="caption" display="block" sx={{ color: 'success.main' }}>
            {newValue}
          </Typography>
        )}
      </Box>
    );
  }

  return <>N/A</>;
};

AuditTrailDetails.propTypes = {
  oldValue: PropTypes.string,
  newValue: PropTypes.string,
};

AuditTrailDetails.defaultProps = {
  oldValue: null,
  newValue: null,
};

export default AuditTrailDetails;
